import {
  subscribeTelephonyEvents,
  telephonyRequest,
  focusMainWindow,
} from "../../../utils/desktopBridge";
import { hasNativeTelephonyBridge } from "../../../utils/isDesktop";
import { resolveNativeSipRegistrarHost } from "./nativeSipRegistrar";

const normalizeEngineEvent = (raw) => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const type = raw.type ?? raw.Type;
  if (!type) {
    return null;
  }
  return {
    type,
    phoneNumber: raw.phoneNumber ?? raw.PhoneNumber,
    direction: raw.direction ?? raw.Direction,
    sipCallId: raw.sipCallId ?? raw.SipCallId,
    reason: raw.reason ?? raw.Reason,
  };
};

const createMockInvitation = (event) => ({
  id: event.sipCallId,
  request: { callId: event.sipCallId },
  remoteIdentity: {
    uri: { user: event.phoneNumber || "Unknown" },
  },
});

/**
 * Native SIP client backed by the local .NET SIPSorcery engine (Wave-like UDP/TCP).
 * Implements the same public API as sip.js SipClient for useBrowserPhone.
 */
export class NativeSipClient {
  constructor(config, handlers = {}) {
    this.config = config;
    this.handlers = handlers;
    this.remoteAudio = handlers.remoteAudio || null;
    this.session = null;
    this.unsubscribeEvents = null;
    this.connected = false;
    this.registrationWait = null;
  }

  updateHandlers(handlers = {}) {
    this.handlers = { ...this.handlers, ...handlers };
    if (handlers.remoteAudio) {
      this.remoteAudio = handlers.remoteAudio;
    }
  }

  bindEventHandlers() {
    if (this.unsubscribeEvents) {
      this.unsubscribeEvents();
    }

    this.unsubscribeEvents = subscribeTelephonyEvents((event) => {
      this.handleEngineEvent(event);
    });
  }

  handleEngineEvent(rawEvent) {
    const event = normalizeEngineEvent(rawEvent);
    if (!event) {
      return;
    }

    switch (event.type) {
      case "registered":
        if (this.registrationWait) {
          this.registrationWait.resolve();
        }
        if (this.handlers.onRegistered) {
          this.handlers.onRegistered();
        }
        break;

      case "registrationFailed": {
        const registrationError = new Error(event.reason || "SIP registration failed");
        if (this.registrationWait) {
          this.registrationWait.reject(registrationError);
        }
        if (this.handlers.onTransportError) {
          this.handlers.onTransportError(registrationError);
        }
        break;
      }

      case "incomingCall": {
        const invitation = createMockInvitation(event);
        this.session = invitation;
        focusMainWindow().catch(() => undefined);
        if (this.handlers.onIncomingCall) {
          this.handlers.onIncomingCall(invitation);
        }
        break;
      }

      case "incomingCancel":
        if (this.handlers.onIncomingCancel) {
          this.handlers.onIncomingCancel({ invitation: this.session });
        }
        this.finalizeSession(event);
        break;

      case "callProgress":
        if (this.handlers.onCallProgress) {
          this.handlers.onCallProgress({
            direction: event.direction || "OUTBOUND",
            phoneNumber: event.phoneNumber,
            session: this.session,
          });
        }
        break;

      case "callAnswered":
        if (this.handlers.onCallEstablished) {
          this.handlers.onCallEstablished({
            direction: event.direction,
            phoneNumber: event.phoneNumber,
            callId: event.sipCallId,
            session: this.session,
          });
        }
        if (this.handlers.onMediaConnected) {
          this.handlers.onMediaConnected({ session: this.session, direction: event.direction });
        }
        break;

      case "callEnded": {
        const rejected =
          event.reason === "rejected" ||
          event.reason === "REJECTED" ||
          event.reason === "decline" ||
          event.reason === "DECLINE";
        if (!rejected && this.handlers.onRemoteBye) {
          this.handlers.onRemoteBye({
            direction: event.direction,
            phoneNumber: event.phoneNumber,
            session: this.session,
          });
        }
        if (rejected && this.handlers.onIncomingCancel) {
          this.handlers.onIncomingCancel({ invitation: this.session });
        }
        this.finalizeSession(event);
        break;
      }

      case "callFailed":
        if (this.handlers.onMediaFailure) {
          this.handlers.onMediaFailure({
            session: this.session,
            direction: event.direction,
            iceState: event.reason,
          });
        }
        this.finalizeSession(event);
        break;

      default:
        break;
    }
  }

  finalizeSession(event) {
    const direction = event?.direction;
    const phoneNumber = event?.phoneNumber;
    const session = this.session;
    const terminateReason = event?.reason;
    if (this.handlers.onCallTerminated) {
      this.handlers.onCallTerminated({ direction, phoneNumber, session, terminateReason });
    }
    this.session = null;
  }

  async connect() {
    if (!hasNativeTelephonyBridge()) {
      throw new Error(
        "Desktop SIP engine is not available. Close the browser tab and use the Electron window, " +
          "and ensure npm run dev includes the SIP engine (port 5099)."
      );
    }

    if (!this.config?.sipDomain) {
      throw new Error(
        "Phone calling is not enabled. Configure UCM SIP domain and agent extension credentials."
      );
    }

    const extension = this.config.extensionUsername || this.config.agentExtension;
    const authUsername =
      this.config.sipAuthorizationUsername || this.config.extensionUsername || extension;
    const password = this.config.sipAuthorizationPassword;
    if (!extension || !password) {
      throw new Error("Agent extension and SIP password are required for calling.");
    }

    this.bindEventHandlers();

    const registrationWait = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        if (!this.registrationWait) {
          return;
        }
        this.registrationWait = null;
        reject(new Error("SIP registration timed out waiting for UCM response."));
      }, 45000);
      this.registrationWait = {
        resolve: () => {
          clearTimeout(timeout);
          this.registrationWait = null;
          resolve();
        },
        reject: (error) => {
          clearTimeout(timeout);
          this.registrationWait = null;
          reject(error);
        },
      };
    });

    const registrarHost = resolveNativeSipRegistrarHost(this.config);
    try {
      const status = await telephonyRequest("POST", "/sip/register", {
        extension,
        authUsername,
        password,
        sipDomain: this.config.sipDomain,
        sipRegistrarHost: registrarHost,
        sipPort: Number(this.config.sipUdpPort) || 5060,
        transport: (this.config.sipTransport || "UDP").toUpperCase(),
        displayName: this.config.displayName || extension,
      });
      const registrationState =
        status?.registrationState ?? status?.RegistrationState ?? "";
      if (String(registrationState).toLowerCase() === "registered") {
        if (this.registrationWait) {
          this.registrationWait.resolve();
        }
      } else {
        await registrationWait;
      }
      this.connected = true;
    } catch (error) {
      if (this.registrationWait) {
        this.registrationWait.reject(error);
      }
      this.registrationWait = null;
      throw error;
    }
  }

  async ensureReady() {
    if (!this.connected) {
      throw new Error("Phone is not connected.");
    }
  }

  async disconnect() {
    await this.hangup().catch(() => undefined);
    try {
      await telephonyRequest("POST", "/sip/unregister");
    } catch {
      // ignore unregister errors during shutdown
    }
    if (this.unsubscribeEvents) {
      this.unsubscribeEvents();
      this.unsubscribeEvents = null;
    }
    this.connected = false;
  }

  releaseStaleSession() {
    if (!this.session) {
      return;
    }
    this.session = null;
  }

  async clearIdleSession() {
    this.session = null;
  }

  async forceEndSession() {
    try {
      await telephonyRequest("POST", "/sip/hangup");
    } catch {
      // ignore
    }
    this.session = null;
    this.stopRemoteAudio();
  }

  async dial(phoneNumber) {
    if (!this.connected) {
      throw new Error("Phone is not connected.");
    }

    await this.clearIdleSession();
    this.session = { direction: "OUTBOUND", phoneNumber };

    await telephonyRequest("POST", "/sip/call", { phoneNumber });
    return this.session;
  }

  async acceptCall() {
    if (!this.session) {
      throw new Error("No incoming call to accept.");
    }
    await telephonyRequest("POST", "/sip/answer");
  }

  async rejectCall() {
    if (!this.session) return;
    try {
      await telephonyRequest("POST", "/sip/reject");
    } finally {
      this.session = null;
    }
  }

  async hangup() {
    if (!this.session) return;
    try {
      await telephonyRequest("POST", "/sip/hangup");
    } finally {
      this.clearSession();
      this.stopRemoteAudio();
    }
  }

  setMuted(muted) {
    telephonyRequest("POST", "/sip/mute", { muted: Boolean(muted) }).catch(() => undefined);
    return true;
  }

  async setHold(_hold) {
    // Hold not yet implemented in native engine
    return false;
  }

  stopRemoteAudio() {
    const audioEl = this.remoteAudio;
    if (!audioEl) return;
    audioEl.srcObject = null;
  }

  clearSession() {
    this.session = null;
  }
}
